import clsx from "clsx";
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute("hidden"));
}

/** Whether a box in the dialog holds something the person wrote. */
function holdsTypedText(root: HTMLElement | null): boolean {
  if (!root) return false;
  const boxes = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>("textarea, input");
  return Array.from(boxes).some((box) => {
    if (box instanceof HTMLInputElement && ["checkbox", "radio", "button", "submit", "file", "range", "color", "hidden"].includes(box.type)) return false;
    return box.value.trim().length > 0;
  });
}

export interface DialogProps {
  open: boolean;
  /** Called for Escape, the Close button and a press on the dim background. */
  onClose: () => void;
  title: string;
  children?: ReactNode;
  /** Buttons along the bottom. */
  footer?: ReactNode;
  size?: "small" | "medium" | "large";
  /**
   * A press on the dim background closes the dialog (the default). A dialog with a form turns it off, because a stray press
   * must not throw away what was typed (UI-15). Even with the default, a press never closes a dialog whose boxes hold text.
   */
  closeOnBackdrop?: boolean;
  className?: string;
}

/**
 * A dialog over the page. Focus moves into it and comes back to where it was, Tab stays inside it,
 * and Escape closes it. Put `data-autofocus` on the control that should have focus first.
 */
export function Dialog({ open, onClose, title, children, footer, size = "medium", closeOnBackdrop = true, className }: DialogProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const before = document.activeElement;
    const panel = panelRef.current;
    if (panel) {
      const wanted = panel.querySelector<HTMLElement>("[data-autofocus]");
      const body = panel.querySelector<HTMLElement>(".dialog__body");
      const first = wanted ?? (body ? focusablesIn(body)[0] : undefined) ?? focusablesIn(panel)[0];
      (first ?? panel).focus();
    }
    return () => {
      if (before instanceof HTMLElement) before.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab" || !panelRef.current) return;
    const items = focusablesIn(panelRef.current);
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget && !holdsTypedText(panelRef.current)) onClose();
      }}
      onKeyDown={onKeyDown}
    >
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={clsx("dialog", `dialog--${size}`, className)}>
        <header className="dialog__head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-button" aria-label="Close" title="Close" onClick={onClose}>
            <span aria-hidden="true">×</span>
          </button>
        </header>
        <div className="dialog__body">{children}</div>
        {footer && <footer className="dialog__foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
